// Pure builders for quoted reply / forward content and reply-all recipients in
// the Tech Support Co-Pilot composer. Kept in a .ts sibling so they're testable
// without rendering the composer.
//
// Conventions mirror the orders-portal ComposeService: replies get a
// Gmail-style `gmail_quote` block ("On <date>, <sender> wrote:" + blockquote);
// forwards get the Outlook-style structured header (From/Date/Subject/To) after
// a "Forwarded message" rule.

import type { ChainDetail, ChainEmail } from '../../types/portal';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function textToHtml(text: string): string {
  return escapeHtml(text).replace(/\n/g, '<br>');
}

/** The message's body as HTML — its own HTML when present, else escaped text. */
function bodyAsHtml(email: ChainEmail): string {
  if (email.body_html) return email.body_html;
  return textToHtml(email.body_text ?? '');
}

function senderLabel(email: ChainEmail): string {
  const name = email.from_name?.trim();
  const addr = email.from_address ?? '';
  if (name && addr && name !== addr) return `${name} <${addr}>`;
  return addr || name || 'unknown sender';
}

const QUOTE_STYLE = 'margin:0 0 0 .8ex;border-left:1px #ccc solid;padding-left:1ex';

/** Gmail-style quoted block for a reply, appended after John's (empty) message. */
export function buildReplyQuoteHtml(email: ChainEmail): string {
  const when = email.received_at ?? '';
  return (
    `<p></p><div class="gmail_quote">On ${escapeHtml(when)}, ${escapeHtml(senderLabel(email))} wrote:<br>` +
    `<blockquote class="gmail_quote" style="${QUOTE_STYLE}">${bodyAsHtml(email)}</blockquote></div>`
  );
}

/** Outlook-style forwarded block (structured headers, no indentation). */
export function buildForwardQuoteHtml(email: ChainEmail): string {
  const rows = [
    `<b>From:</b> ${escapeHtml(senderLabel(email))}`,
    email.received_at ? `<b>Date:</b> ${escapeHtml(email.received_at)}` : null,
    `<b>Subject:</b> ${escapeHtml(email.subject ?? '(no subject)')}`,
    email.to_address ? `<b>To:</b> ${escapeHtml(email.to_address)}` : null,
    email.cc_addresses ? `<b>Cc:</b> ${escapeHtml(email.cc_addresses)}` : null,
  ].filter(Boolean);
  return (
    `<p></p><div class="gmail_quote">---------- Forwarded message ---------<br>` +
    `${rows.join('<br>')}<br><br>${bodyAsHtml(email)}</div>`
  );
}

/** `Fwd:` subject (idempotent — an existing Fwd:/FW: prefix is kept as-is). */
export function forwardSubject(subject: string | null | undefined): string {
  const s = (subject ?? '').trim();
  if (/^(fwd?|fw):/i.test(s)) return s;
  return `Fwd: ${s}`;
}

/** Split a raw address header ("A <a@x>, b@y") into bare email addresses. */
export function extractAddresses(header: string | null | undefined): string[] {
  if (!header) return [];
  const out: string[] = [];
  for (const part of header.split(',')) {
    const angled = part.match(/<([^>]+)>/);
    const candidate = (angled ? angled[1] : part).trim();
    if (candidate && candidate.includes('@')) out.push(candidate);
  }
  return out;
}

/**
 * Reply-all recipients for a message: To = the sender; Cc = everyone else on
 * the message (its To + Cc) minus our own mailbox and minus the sender.
 */
/**
 * The address a reply on `thread` should go to.
 *
 * The server resolves this (`ChainDetail.reply_recipient`) because the sender is
 * the wrong answer on the two shapes this mailbox is full of: contact-form leads
 * arrive from `noreply@chemmasters.net`, and an already-answered thread's newest
 * message is our own. Never re-derive it from the sender here.
 *
 * - a string    -> use it
 * - `null`      -> the server found no replyable address; return '' so the
 *                  composer opens empty and a human supplies one
 * - `undefined` -> a backend predating the field; fall back to the sender
 */
export function replyRecipient(
  thread: Pick<ChainDetail, 'reply_recipient'>,
  fallbackSender: string | null | undefined,
): string {
  if (thread.reply_recipient !== undefined) return thread.reply_recipient ?? '';
  return fallbackSender ?? '';
}

export function replyAllRecipients(
  email: ChainEmail,
  accountEmail: string,
  resolvedTo: string,
): { to: string; cc: string[] } {
  // `resolvedTo` is the server-resolved reply recipient, NOT email.from_address:
  // a contact-form lead is sent by a no-reply box, and an already-answered
  // thread's newest message is our own. Cc still comes off the message.
  const to = resolvedTo;
  const self = accountEmail.toLowerCase();
  const seen = new Set<string>([self, to.toLowerCase()]);
  const cc: string[] = [];
  for (const addr of [
    ...extractAddresses(email.to_address),
    ...extractAddresses(email.cc_addresses),
  ]) {
    const key = addr.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    cc.push(addr);
  }
  return { to, cc };
}
